const moment = require('moment');
const { get, find } = require('lodash');

const plans = require('./plans');

const { ANNUAL_DISCOUNT } = plans;

const INTERVALS = Object.freeze({
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
});

const DISCOUNT_VALUE_TYPE = Object.freeze({
  PERCENT: 'percent',
  FIXED: 'fixed',
});

function isMonthlyPlan(interval) {
  return interval.startsWith(INTERVALS.MONTHLY);
}

function isAnnualPlan(interval) {
  return interval.startsWith(INTERVALS.YEARLY);
}

function canGoLive(plan) {
  // Sandbox plans cannot go live
  return !isSandbox(plan);
}

function isSandbox(plan) {
  const planId = typeof plan === 'string' ? plan : get(plan, 'metadata.id');
  return planId === 'partner-sandbox' || isOldSandbox(plan);
}

function isOldSandbox(plan) {
  const planId = typeof plan === 'string' ? plan : get(plan, 'metadata.id');
  if (
    planId === 'sandbox' ||
    planId === 'sandbox-plus' ||
    planId === 'sandbox2'
  ) {
    return true;
  }
  return false;
}

function billingTrialDaysLeft(plan = null) {
  if (plan && plan.trial) {
    const remaining = moment(plan.date_trial_end).diff(
      Date.now(),
      'days',
      true,
    );
    if (remaining > 1) {
      return Math.ceil(remaining);
    }
    return 0;
  }
  return null;
}

function billingTrialDaysLeftMessage(plan = null) {
  if (plan && plan.trial) {
    const remaining = moment(plan.date_trial_end).diff(
      Date.now(),
      'days',
      true,
    );
    if (remaining > 1) {
      return `${Math.ceil(remaining)} days left`;
    } else if (remaining > 0.5) {
      return '1 day left';
    } else if (remaining > 0) {
      return 'a few hours left';
    }
  }
  return null;
}

function billingDiscountByInterval(
  product,
  interval = INTERVALS.MONTHLY,
  intervalCount = 1,
) {
  const productMetaId = product.metadata?.id || product.id;
  if (productMetaId === 'enterprise') {
    return 0;
  }
  if (isAnnualPlan(interval)) {
    return Math.round(
      ANNUAL_DISCOUNT * intervalCount - (intervalCount - 1) * 5,
    );
  }
  return 0;
}

function billingPriceByInterval(
  product,
  interval = INTERVALS.MONTHLY,
  intervalCount = 1,
  account = null,
) {
  if (!product) {
    return 0;
  }
  let price = billingPriceWithPromo(product, interval, intervalCount, account);

  if (isAnnualPlan(interval)) {
    const intervalMonths = intervalCount * 12;
    const discountPercent = billingDiscountByInterval(
      product,
      interval,
      intervalCount,
    );

    return Math.round(
      (price - price * (discountPercent / 100)) * intervalMonths,
    );
  }

  return Math.round(price);
}

// total discount
function billingCouponDiscountPercent(coupon) {
  if (!coupon) {
    return 0;
  }
  const discount = coupon.discounts?.find(
    (d) => d.type === 'total' && d.value_type === DISCOUNT_VALUE_TYPE.PERCENT,
  );
  return discount ? discount.value_percent : 0;
}

// product own discount
function billingCouponFindProductDiscount(coupon, productId) {
  if (!coupon || !productId) {
    return null;
  }
  return (
    coupon.discounts?.find(
      (d) =>
        d.type === 'product' &&
        (d.value_type === DISCOUNT_VALUE_TYPE.PERCENT ||
          d.value_type === DISCOUNT_VALUE_TYPE.FIXED) &&
        d.product?.metadata?.id === productId,
    ) || null
  );
}

function billingCouponProductHasDiscount(coupon, productId) {
  const discount = billingCouponFindProductDiscount(coupon, productId);
  if (!discount) {
    return false;
  }

  switch (discount.value_type) {
    case DISCOUNT_VALUE_TYPE.PERCENT:
      return discount.value_percent > 0;
    case DISCOUNT_VALUE_TYPE.FIXED:
      return discount.value_fixed > 0;
    default:
  }

  return false;
}

function billingPriceByMonth(
  product,
  interval = INTERVALS.MONTHLY,
  intervalCount = 1,
  account = null,
) {
  const price = billingPriceWithPromo(
    product,
    interval,
    intervalCount,
    account,
  );

  if (isAnnualPlan(interval)) {
    const discountPercent = billingDiscountByInterval(
      product,
      interval,
      intervalCount,
    );
    return Math.round(price - price * (discountPercent / 100));
  }
  return Math.round(price);
}

// not used
function billingPriceByYear(
  product,
  interval = INTERVALS.MONTHLY,
  intervalCount = 1,
  account = null,
) {
  const price = billingPriceWithPromo(
    product,
    interval,
    intervalCount,
    account,
  );
  if (isMonthlyPlan(interval)) {
    return Math.round(price * 12);
  }
  return Math.round(price);
}

function billingPriceWithPromo(
  product,
  interval = INTERVALS.MONTHLY,
  intervalCount = 1,
  account = null,
) {
  const productMetaId = product.metadata?.id || product.id;

  let price = product.price || 0;
  if (account?.pricing) {
    const accountPricingPlan = find(account.pricing, { id: productMetaId });
    if (accountPricingPlan && accountPricingPlan.price !== null) {
      price = accountPricingPlan.price || 0;
    }
  }

  // no discounts
  if (!account?.plan?.coupon_code || !account.plan?.coupon) {
    return price;
  }

  let actualPrice = price;
  const totalDiscountPercent = billingCouponDiscountPercent(
    account.plan.coupon,
  );
  // prefer total percent discount
  if (totalDiscountPercent > 0) {
    actualPrice = price - price * (totalDiscountPercent / 100);
    return actualPrice;
  }

  const productDiscount = billingCouponFindProductDiscount(
    account.plan.coupon,
    productMetaId,
  );

  if (!productDiscount) {
    return actualPrice;
  }

  // product own discount
  switch (productDiscount.value_type) {
    case DISCOUNT_VALUE_TYPE.PERCENT: {
      const discountAmount = price * (productDiscount.value_percent / 100);
      const effectiveDiscount =
        productDiscount.discount_max > 0
          ? Math.min(discountAmount, productDiscount.discount_max)
          : discountAmount;
      actualPrice = price - effectiveDiscount;
      if (effectiveDiscount >= discountAmount) {
        return actualPrice;
      }

      // if we use the discount_max, this is equal to the fixed discount:  we should distribute it over months
      break;
    }
    case DISCOUNT_VALUE_TYPE.FIXED: {
      const fixedAmount = productDiscount.value_fixed || 0;
      actualPrice = Math.max(0, price - fixedAmount);
      break;
    }
    default:
  }

  if (isMonthlyPlan(interval)) {
    return actualPrice;
  }

  // for yearly price we should distribute fixed discount over months
  const monthsCount = intervalCount * 12;
  const coef = 1 / monthsCount;
  const discount = price - actualPrice;
  return price - discount * coef;
}

function getUsesText(isMonthly, account, planId) {
  const coupon = account.plan?.coupon;
  if (!account.plan?.coupon_code || !coupon?.limit_subscription_uses) {
    return null;
  }

  const totalDiscount = billingCouponDiscountPercent(coupon);
  const productHasDiscount = billingCouponProductHasDiscount(coupon, planId);
  if (!totalDiscount && !productHasDiscount) {
    return null;
  }
  const uses = coupon.limit_subscription_uses;
  const unit = isMonthly
    ? uses === 1
      ? 'month'
      : 'months'
    : uses === 1
    ? 'year'
    : 'years';

  return `for first ${uses > 1 ? `${uses} ${unit}` : `${unit}`}`;
}

/**
 * Resolves the effective feature flags of a billing subscription, the same
 * way the account plan does: plan defaults, overridden by the subscription
 * product's features, overridden by the subscription's own (unexpired)
 * features.
 *
 * @param {object} [subscription] Subscription with `product` expanded
 * @returns {object}
 */
function getSubscriptionFeatures(subscription) {
  const product = subscription?.product;
  const metaPlan = find(plans.PLANS, { id: product?.metadata?.id });

  let subscriptionMetadata = subscription?.metadata || {};
  if (
    subscriptionMetadata.date_expired &&
    subscriptionMetadata.date_expired < Date.now()
  ) {
    subscriptionMetadata = {};
  }

  return {
    ...metaPlan?.features,
    ...product?.metadata?.features,
    ...subscriptionMetadata.features,
  };
}

module.exports = {
  ...plans,
  INTERVALS,
  billingTrialDaysLeft,
  billingTrialDaysLeftMessage,
  billingDiscountByInterval,
  billingPriceByInterval,
  billingPriceByMonth,
  billingPriceByYear,
  billingPriceWithPromo,
  billingCouponDiscountPercent,
  billingCouponProductHasDiscount,
  getUsesText,
  canGoLive,
  isSandbox,
  isOldSandbox,
  getSubscriptionFeatures,
};
